import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { apiWorkspaceFiles, apiWorkspaceUsage, apiWorkspaceDelete } from "@/lib/api";

import PortalNav from "@/components/portal/PortalNav";
import BottomTabNav from "@/components/portal/BottomTabNav";
import GridBackground from "@/components/primitives/GridBackground";
import DropZone from "@/components/portal/workspace/DropZone";
import FolderList from "@/components/portal/workspace/FolderList";
import FileRow from "@/components/portal/workspace/FileRow";
import StorageIndicator from "@/components/portal/workspace/StorageIndicator";

export default function Workspace() {
    const { user, loading: authLoading, openAuthModal } = useAuth();
    const [search, setSearch] = useSearchParams();
    const nav = useNavigate();
    const [selected, setSelected] = useState(search.get("module") || "A");
    const [files, setFiles] = useState([]);
    const [usage, setUsage] = useState({ used_bytes: 0, quota_bytes: 0 });
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !user) openAuthModal("login");
    }, [authLoading, user, openAuthModal]);

    const refreshUsage = async () => {
        try { setUsage(await apiWorkspaceUsage()); } catch {}
    };

    const refreshAll = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const all = await apiWorkspaceFiles();
            const byMod = {};
            (all.files || []).forEach((f) => { byMod[f.module_letter] = (byMod[f.module_letter] || 0) + 1; });
            setCounts(byMod);
            const r = await apiWorkspaceFiles(selected);
            setFiles(r.files || []);
            await refreshUsage();
        } finally { setLoading(false); }
    };

    useEffect(() => { refreshAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user, selected]);

    const onUploaded = (meta) => {
        setFiles((f) => [meta, ...f]);
        setCounts((c) => ({ ...c, [meta.module_letter]: (c[meta.module_letter] || 0) + 1 }));
        refreshUsage();
    };

    const onDelete = async (file) => {
        try {
            await apiWorkspaceDelete(file.id);
            setFiles((f) => f.filter((x) => x.id !== file.id));
            setCounts((c) => ({ ...c, [file.module_letter]: Math.max(0, (c[file.module_letter] || 1) - 1) }));
            refreshUsage();
            toast.success("File removed");
        } catch (e) { toast.error("Delete failed"); }
    };

    if (!user) {
        return <div className="min-h-screen flex items-center justify-center bg-tca-deep text-tca-ink"><Loader2 className="animate-spin text-tca-cyan" /></div>;
    }

    const ent = user.entitlements || {};
    const includeIZ = !!(ent.genesis_iz_early_access || ent.tier === "genesis");

    return (
        <div data-testid="workspace-page" className="relative min-h-screen bg-tca-deep text-tca-ink overflow-x-hidden pb-20 lg:pb-0">
            <GridBackground fine />
            <PortalNav />

            <main className="relative z-10 max-w-[1300px] mx-auto px-5 sm:px-8 py-8">
                <button
                    type="button"
                    onClick={() => nav("/portal")}
                    data-testid="workspace-back"
                    className="inline-flex items-center gap-2 font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted hover:text-tca-cyan"
                >
                    <ArrowLeft size={12} /> Back to portal
                </button>

                <div className="mt-5 flex items-baseline justify-between gap-4 flex-wrap">
                    <div>
                        <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/85">// Workspace</div>
                        <h1 className="mt-2 font-display text-[36px] sm:text-[48px] leading-[1.05] tracking-[-0.025em]">
                            <em className="not-italic neon-cyan">Files</em>, mapped to your modules.
                        </h1>
                    </div>
                    <div className="w-full sm:w-64">
                        <StorageIndicator used={usage.used_bytes} quota={usage.quota_bytes} />
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
                    <aside className="bg-tca-bg-panel/55 border border-tca-cyan/15 backdrop-blur-md">
                        <FolderList
                            selected={selected}
                            onSelect={(L) => { setSelected(L); setSearch({ module: L }); }}
                            fileCounts={counts}
                            includeIZ={includeIZ}
                        />
                    </aside>

                    <section className="space-y-6">
                        <DropZone moduleLetter={selected} onUploaded={onUploaded} />

                        <div data-testid="file-list-section">
                            <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/75 mb-3">
                                // {selected} files · {files.length}
                            </div>
                            {loading ? (
                                <div className="flex justify-center py-12"><Loader2 size={20} className="text-tca-cyan animate-spin" /></div>
                            ) : files.length === 0 ? (
                                <div className="text-tca-ink-muted text-sm text-center py-12 border border-tca-ink-dim/30">
                                    No files in this folder yet. Drop something above to begin.
                                </div>
                            ) : (
                                <div className="border border-tca-cyan/12 bg-tca-bg-panel/50">
                                    {files.map((f) => <FileRow key={f.id} file={f} onDelete={onDelete} />)}
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </main>

            <BottomTabNav />
        </div>
    );
}
